/**
 * Charts — BarChart (L2). Pure inline SVG (the Flutter twin is CustomPaint):
 * no chart library, no canvas, no external font.
 *
 * CaloryRing used to live here as native L3 code. It is now a DATA
 * composition (`viewir/compositions.js`) of the generic `Gauge` primitive
 * (`components/viz.jsx`) + `Overlay` + `Text` + `Badge` — which is the whole
 * point of the Composed layer: it ships as data and hot-updates, while the
 * pixels it is made of stay generic and baked.
 *
 * The honesty rules live in the components, not in the blueprint:
 *  - a null value is UNKNOWN and renders as "—", never as 0;
 *  - over-target gets its own colour rather than a clipped bar;
 *  - a bar past the end of the window is inert (no tap, no fill).
 *
 * NO DOMAIN VOCABULARY, including in what it SAYS. This chart passed the
 * registry's genericity rule for years while its own source read "Calories per
 * day this week", `unit || 'cal'` and "Previous week" — the contract was
 * generic and the pixels were not, which is the half of the rule a
 * string-only check cannot see. The copy is props now, with neutral defaults:
 *   `unit`         printed after a bar's value in its accessible name ('')
 *   `pageLabel`    the noun ‹ › steps by, for their names ('page')
 *   `windowLabel`  the caption over the series ('' unless the data names one)
 *   `label`        the chart's own accessible name ('Bar chart')
 * A widget that means weeks says so in its blueprint; the kit does not
 * presume it.
 */
import { resetButton, fmt, toneColor, toneSoft } from './ui';
import { num } from '../paths';
import { ownCard } from './ownCard';

const DAY_MS = 86400000;

/**
 * A bar with a ROUNDED TOP and a flat foot. `rx` on a `<rect>` rounds all four
 * corners, which lifts a bar off its own baseline; a column standing on a chart
 * floor is rounded where it ends, not where it starts. The radius is clamped to
 * half the height so a nearly-empty day draws a cap instead of a crescent.
 */
function barPath(x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius, w / 2, h));
  return `M${x},${y + h} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${y + h} Z`;
}

/** "2026-08-04" + n days → "2026-08-11". Anything unparseable → null. */
function shiftDay(iso, days) {
  const t = Date.parse(`${String(iso || '').slice(0, 10)}T12:00:00`);
  if (Number.isNaN(t)) return null;
  const d = new Date(t + days * DAY_MS);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** SEALED: the dashed target line + inert future days. Week paging via ‹ ›. */
export function BarChart({ props = {}, data, emit, slots }) {
  const setBars = slots?.setBars;
  const source = setBars?.payload?.bars ?? (props.bars !== undefined ? props.bars : data?.bars);
  const bars = Array.isArray(source) ? source : [];
  const target = props.target != null ? num(props.target) : (data?.target != null ? num(data.target) : null);
  const selected = props.selected ?? data?.anchor ?? null;

  /**
   * Paging moves the ANCHOR, not the bars: the series is derived from a date
   * (`weekBars(args.anchor)`), so a bare `{delta}` has nothing to act on and a
   * page connection wired to it is inert. Resolving the new anchor HERE — one
   * window = one page, however many bars the window holds — is what lets a
   * blueprint wire `pageChanged → page_args.<date arg>` and have the chart,
   * the header and the list move together, exactly like a bar tap does.
   */
  const anchor = selected || data?.anchor || bars[0]?.date || null;
  const nextAnchor = shiftDay(anchor, bars.length || 7);

  /**
   * `maxDate` — THE FUTURE CLAMP. A window is derived from an anchor date, so
   * `›` will happily page into next week and draw seven empty bars for days
   * that cannot hold data. Prod's `›` simply stops. The chart already refuses
   * to let you TAP a future day (`b.future`); this is the same rule applied to
   * the window, and it is a prop rather than a rule because only the blueprint
   * knows whether "today" is the ceiling (a log) or not (a forecast).
   */
  const maxDate = props.maxDate ? String(props.maxDate).slice(0, 10) : null;
  const atEnd = Boolean(maxDate && nextAnchor && nextAnchor > maxDate);

  const page = (delta) => {
    if (delta > 0 && atEnd) return;
    const next = shiftDay(anchor, delta * (bars.length || 7));
    emit?.('pageChanged', next ? { delta, date: next } : { delta });
  };

  // The COPY is the blueprint's, not the kit's. `data.window_label` is the
  // data lane's own answer (a derive that knows what a window means may name
  // it); `week_start` stays readable for the derive that emits it, printed
  // neutrally rather than as a sentence about weeks.
  const pageLabel = props.pageLabel || 'page';
  // `title` is the header heading — default 'This week' (the copy the standalone
  // chart always meant); a composition owning the card passes its own.
  const title = props.title !== undefined ? String(props.title) : 'This week';
  const windowLabel = props.windowLabel
    ?? data?.window_label
    ?? (data?.week_start ? `from ${data.week_start}` : '');
  const unit = props.unit ? ` ${props.unit}` : '';
  // `surface: false` is BARE mode — no card, so a composition (WeekReview) owns
  // the surface. The web chart is bare by nature (a PageShell dashboard panel
  // draws its card), so the prop only ever confirms that; the Flutter twin drops
  // its own `ViewIrSurface`. Default (undeclared) is unchanged on both.

  const W = 320;
  const H = 140;
  const padB = 20;
  // headroom for the SELECTED bar's own value, printed over it — without it a
  // full day's label is clipped by the top of the viewBox
  const padT = 14;
  const plot = H - padB - padT;
  const max = Math.max(target || 0, ...bars.map((b) => num(b.value)), 1) * 1.15;
  const slot = bars.length ? W / bars.length : W;
  const barW = Math.min(26, slot * 0.52);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {/* Header: title on the LEFT, the ‹ › pager together on the RIGHT, the two
          VERTICALLY CENTERED (align-items: center). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 'var(--t-md)', fontWeight: 700,
          letterSpacing: '-0.15px', color: 'var(--text)', fontFamily: 'var(--font-display)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</span>
        {windowLabel ? (
          <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-3)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {windowLabel}
          </span>
        ) : null}
        {props.pager === false ? null : (
          <>
            <button type="button" aria-label={`Previous ${pageLabel}`} onClick={() => page(-1)}
                    style={{ ...resetButton, color: 'var(--text-2)', fontSize: 18, padding: '0 4px' }}>‹</button>
            <button type="button" aria-label={`Next ${pageLabel}`} onClick={() => page(1)}
                    disabled={atEnd} aria-disabled={atEnd || undefined}
                    style={{
                      ...resetButton, fontSize: 18, padding: '0 4px',
                      color: atEnd ? 'var(--text-3)' : 'var(--text-2)',
                      opacity: atEnd ? 0.4 : 1,
                      cursor: atEnd ? 'default' : 'pointer',
                    }}>›</button>
          </>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={props.label || 'Bar chart'}>
        {target ? (
          <g>
            <line
              x1="0" x2={W}
              y1={H - padB - (plot * target) / max}
              y2={H - padB - (plot * target) / max}
              stroke="var(--line)" strokeWidth="1" strokeDasharray="3 5"
            />
          </g>
        ) : null}
        {bars.map((b, i) => {
          const v = num(b.value);
          const h = max ? (plot * v) / max : 0;
          const x = i * slot + (slot - barW) / 2;
          const y = H - padB - h;
          const isSel = selected != null && b.date === selected;
          // ACCENT is what the eye should land on: the day being read, and any
          // day that got where it was aiming. Everything else is track — a
          // future day (inert) and an ordinary one both sit quiet, and what
          // separates them is the bar's own height, not a third colour.
          const reached = !b.future && target != null && v >= target;
          const lit = !b.future && (isSel || reached);
          const fill = lit ? 'var(--accent)' : 'var(--surface-3)';
          return (
            <g key={b.date}
               // A tappable bar is a control, so it carries a control's
               // accessible name. A future day is inert and gets neither.
               role={b.future ? undefined : 'button'}
               aria-label={b.future ? undefined : `${b.date}: ${v}${unit}`}
               onClick={b.future ? undefined : () => emit?.('barSelected', { date: b.date })}
               style={{ cursor: b.future ? 'default' : 'pointer' }}>
              <rect x={i * slot} y={0} width={slot} height={H} fill="transparent" />
              <path d={barPath(x, Math.min(y, H - padB - 2), barW, Math.max(h, 2), 5)} fill={fill} />
              {isSel && v > 0 ? (
                <text
                  x={i * slot + slot / 2} y={Math.max(y - 5, 9)} textAnchor="middle"
                  fontSize="10" fontWeight="700" fill="var(--accent)"
                >{fmt(v)}</text>
              ) : null}
              <text
                x={i * slot + slot / 2} y={H - 6} textAnchor="middle"
                fontSize="10" fontWeight={isSel ? 700 : 500}
                fill={isSel ? 'var(--accent)' : 'var(--text-3)'}
              >{b.label}</text>
            </g>
          );
        })}
      </svg>
      {/* PER-COLUMN THUMBNAILS. One flex column per bar (flex:1, no h-padding) so
          column i sits under SVG slot i; each column stacks its bar's `thumbs`
          newest-first, circular, at ~the bar's width (52% of the column = the
          same `slot*0.52` ratio as `barW`). A bar with no thumbs is an empty
          column, which keeps every column aligned to its bar. A tap carries the
          tapped row so a wire binds `item.item_id` / `item.session_id` /
          `item.log_card_id`. */}
      {bars.some((b) => Array.isArray(b.thumbs) && b.thumbs.length) ? (
        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
          {bars.map((b) => (
            <div
              key={b.date}
              style={{
                flex: 1, minWidth: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 'var(--sp-1)',
              }}
            >
              {(Array.isArray(b.thumbs) ? b.thumbs : []).map((t, i) => (
                <button
                  type="button"
                  key={t.item_id ?? t.log_card_id ?? `${b.date}-${i}`}
                  aria-label={`Open ${b.date}`}
                  onClick={() => emit?.('thumbActivated', { item_id: t.item_id, ...t }, { item: t })}
                  style={{ ...resetButton, width: '52%', cursor: 'pointer' }}
                >
                  <img
                    src={t.image_url} alt=""
                    style={{ width: '100%', aspectRatio: '1', borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                  />
                </button>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}


/**
 * DayCells (L1) — a month laid out as seven columns of day cells.
 *
 * It draws the days it is HANDED: no bucketing, no counting, no date math.
 * That is the line between it and `ItemCalendar` (L2), which takes rows plus a
 * date field and works all of that out itself.
 *
 * A cell shows its `glyph` when the day carries one and its `label` otherwise —
 * so the month reads as WHAT HAPPENED, with the bare dates as quiet background.
 * The corner badge counts only 2+ (a "1" badge over a single glyph says nothing
 * the glyph did not already say).
 *
 * Colors come from tokens, never from the design's literals: a cell hardcoded
 * to #F9F9F9 on white is invisible in dark mode.
 */
function DayCellsBare({ props = {}, data, emit, slots }) {
  const pushed = slots?.setDays?.payload?.days;
  const source = pushed ?? (props.days !== undefined ? props.days : data?.days ?? data);
  const days = Array.isArray(source) ? source : [];
  const weekdays = Array.isArray(props.weekdays) ? props.weekdays : [];
  const columns = Math.max(1, Math.round(num(props.columns ?? 7) || 7));
  const pickedDate = slots?.setSelected?.payload?.date;

  if (!days.length && !weekdays.length) return null;

  const cell = (day, i) => {
    const date = day?.date ?? null;
    const selected = pickedDate != null ? pickedDate === date : Boolean(day?.selected);
    const muted = Boolean(day?.muted);
    const glyph = day?.glyph ? String(day.glyph) : '';
    const count = num(day?.count);
    const label = day?.label == null ? '' : String(day.label);
    const blank = !glyph && !label;
    const tappable = Boolean(emit) && !muted && !blank && date != null;

    return (
      <div
        key={`${date ?? 'blank'}-${i}`}
        data-testid="day-cell"
        data-selected={selected ? '1' : '0'}
        role={tappable ? 'button' : undefined}
        tabIndex={tappable ? 0 : undefined}
        onClick={tappable ? () => emit('daySelected', { date, index: i }) : undefined}
        style={{
          position: 'relative', aspectRatio: '1 / 1', minWidth: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 12,
          // A glyph day stands on the page itself; only a bare date needs a chip
          // behind it, which is what makes a month of glyphs read as content.
          // A BLANK cell (no glyph, no label) is the padding a month needs to
          // start on the right weekday — it draws nothing at all, because a chip
          // there reads as a date that went missing.
          background: blank
            ? 'transparent'
            : (selected ? 'var(--text)' : (glyph ? 'transparent' : 'var(--surface-3)')),
          opacity: muted ? 0.35 : 1,
          cursor: tappable ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <span style={{
          fontSize: glyph ? 'var(--t-xl)' : 'var(--t-sm)',
          fontWeight: 700,
          lineHeight: 1,
          color: selected ? 'var(--surface)' : 'var(--text)',
        }}>{glyph || label}</span>
        {count > 1 ? (
          <span
            data-testid="day-badge"
            style={{
              position: 'absolute', top: -4, right: -4,
              minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--text)', color: 'var(--surface)',
              border: '2px solid var(--surface)',
              fontSize: 'var(--t-xs)', fontWeight: 700, boxSizing: 'border-box',
            }}
          >{fmt(count)}</span>
        ) : null}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0 }}>
      {weekdays.length ? (
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 'var(--sp-2)',
        }}>
          {weekdays.map((w, i) => (
            <span key={`${w}-${i}`} style={{
              textAlign: 'center', fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--text-3)',
            }}>{w}</span>
          ))}
        </div>
      ) : null}
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 'var(--sp-2)',
      }}>
        {days.map(cell)}
      </div>
    </div>
  );
}

/**
 * StatHeadline (L1) — one number said loudly, with the context that makes it
 * mean something: an eyebrow over it, the unit on its baseline, a second
 * reading under it, and an optional shingled row of glyphs on the trailing
 * side.
 *
 * The shingle is the point of the glyph row: a handful of things read as a
 * PILE, not as a list — laid out with a negative margin rather than a gap, and
 * capped so a big month does not run off the card.
 *
 * `StatTileRow` is the sibling for SEVERAL comparable counts that filter a
 * grid; this one is a single headline and is inert. A null value is UNKNOWN
 * and prints an em dash — never a zero.
 */
function StatHeadlineBare({ props = {}, data, slots }) {
  const pushed = slots?.setValue?.payload;
  const rawValue = pushed?.value !== undefined
    ? pushed.value
    : (props.value !== undefined ? props.value : data?.value);
  const known = rawValue != null && rawValue !== '';
  // A STRING value passes through verbatim ("Mar 4" is a reading, not 4):
  // `num()` strips non-digits, so it must not decide what is numeric here.
  const numeric = typeof rawValue === 'number'
    || (typeof rawValue === 'string' && rawValue.trim() !== '' && Number.isFinite(Number(rawValue)));
  const value = !known ? '—' : (numeric ? fmt(num(rawValue)) : String(rawValue));

  const unit = props.unit ?? data?.unit ?? null;
  const eyebrow = props.eyebrow ?? data?.eyebrow ?? null;
  const sub = pushed?.sub ?? props.sub ?? data?.sub ?? null;
  const glyphSource = pushed?.glyphs ?? (props.glyphs !== undefined ? props.glyphs : data?.glyphs);
  const cap = Math.max(1, Math.round(num(props.maxGlyphs ?? 6) || 6));
  const glyphs = (Array.isArray(glyphSource) ? glyphSource : [])
    .map((g) => (g && typeof g === 'object' ? g.glyph ?? g.label ?? '' : g))
    .filter(Boolean)
    .slice(0, cap);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 'var(--sp-3)', minWidth: 0,
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', minWidth: 0 }}>
        {eyebrow ? (
          <span style={{
            fontSize: 'var(--t-sm)', fontWeight: 700, color: 'var(--text-3)',
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{eyebrow}</span>
        ) : null}
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)', minWidth: 0 }}>
          <span style={{
            fontSize: 'var(--t-display)', fontWeight: 700, lineHeight: 1.05,
            letterSpacing: '-0.02em',
            color: known ? toneColor(props.tone) : 'var(--text-3)',
          }}>{value}</span>
          {unit ? (
            <span style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--text-3)' }}>{unit}</span>
          ) : null}
        </span>
        {sub ? (
          <span style={{
            fontSize: 'var(--t-xs)', fontWeight: 500, color: 'var(--text-3)',
            minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{sub}</span>
        ) : null}
      </div>
      {glyphs.length ? (
        <span
          data-testid="headline-glyphs"
          aria-hidden
          style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', paddingLeft: 'var(--sp-2)' }}
        >
          {glyphs.map((g, i) => (
            <span
              key={`${g}-${i}`}
              data-testid="headline-glyph"
              style={{
                fontSize: 'var(--t-display)', lineHeight: 1,
                // the shingle: each glyph overlaps the one before it
                marginLeft: i === 0 ? 0 : -18,
                textShadow: '2px 0 0 var(--surface)',
              }}
            >{g}</span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

/**
 * DayBars (L1) — a short span of days read as one glance: a caption over a bar
 * per day, each filled against the tallest in the set.
 *
 * It is DayCells at a week's scale, and neither is BarChart (a series chart with
 * a target line, paging and selection). It draws the days it is handed.
 *
 * SEALED: a day with no value is an EMPTY TRACK, never a zero-height bar — a
 * missing reading and a reading of zero are different claims.
 */
function DayBarsBare({ props = {}, data, emit, slots }) {
  const pushed = slots?.setDays?.payload?.days;
  const source = pushed ?? (props.days !== undefined ? props.days : data?.days ?? data);
  const days = Array.isArray(source) ? source : [];
  if (!days.length) return null;

  const values = days.map((d) => (d?.value == null ? null : num(d.value))).filter((v) => v != null);
  const ceiling = num(props.max) || (values.length ? Math.max(...values) : 0);
  const height = Math.max(8, Math.round(num(props.height ?? 32) || 32));
  const fill = toneColor(props.tone);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
      {days.map((day, i) => {
        const raw = day?.value == null ? null : num(day.value);
        const pct = raw == null || ceiling <= 0 ? 0 : Math.max(0, Math.min(1, raw / ceiling));
        const tappable = Boolean(emit) && day?.key != null;
        return (
          <div
            key={`${day?.key ?? day?.label ?? i}-${i}`}
            data-testid="day-bar"
            data-fill={String(Math.round(pct * 100))}
            role={tappable ? 'button' : undefined}
            onClick={tappable ? () => emit('daySelected', { key: day.key ?? null, index: i }) : undefined}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5,
              flex: 1, minWidth: 0, cursor: tappable ? 'pointer' : 'default',
            }}
          >
            <span style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--text-3)' }}>
              {day?.label ?? ''}
            </span>
            <span style={{
              width: 14, height, borderRadius: 6, background: 'var(--surface-3)',
              display: 'flex', alignItems: 'flex-end', overflow: 'hidden',
              boxShadow: day?.emphasis ? '0 0 0 3px rgba(0,0,0,0.08)' : undefined,
            }}>
              <span style={{ width: '100%', height: `${pct * 100}%`, background: fill, borderRadius: 6 }} />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * TallyCard (L1) — one count of things waiting on the user, and the one thing
 * to do about them.
 *
 * Header · the value set LARGE in the monospace face (a tally is a number you
 * read, not a word you skim) beside an uppercase eyebrow and a line of copy ·
 * ONE full-width action. Where StatHeadline reads a number in context and is
 * inert, this one exists to be acted on. Its spacing is its own — the pixel
 * values are the design's. A null value is UNKNOWN: an em dash, never a zero.
 */
function TallyCardBare({ props = {}, data, emit, slots }) {
  const pushed = slots?.setTally?.payload || {};
  const rawValue = pushed.value !== undefined ? pushed.value : (props.value !== undefined ? props.value : data?.value);
  const known = rawValue != null && rawValue !== '';
  const numeric = typeof rawValue === 'number'
    || (typeof rawValue === 'string' && rawValue.trim() !== '' && Number.isFinite(Number(rawValue)));
  const value = !known ? '—' : (numeric ? fmt(num(rawValue)) : String(rawValue));
  const header = props.header ?? (data ? data.header : null);
  const eyebrow = props.eyebrow ?? (data ? data.eyebrow : null);
  const text = pushed.text ?? props.text ?? (data ? data.text : null);
  const action = props.action ?? (data ? data.action : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {header ? (
        <span style={{
          fontSize: 20, fontWeight: 800, letterSpacing: -0.5, color: 'var(--text)', marginBottom: 20,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{header}</span>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
        <span data-testid="tally-value" style={{
          fontSize: 48, lineHeight: 1, letterSpacing: -2, fontWeight: 700, flex: '0 0 auto',
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          color: known ? 'var(--text)' : 'var(--text-3)',
        }}>{value}</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
          {eyebrow ? (
            <span style={{
              fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--text-3)',
            }}>{eyebrow}</span>
          ) : null}
          {text ? (
            <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, color: 'var(--text)' }}>{text}</span>
          ) : null}
        </div>
      </div>
      {action && action.label ? (
        <button
          type="button"
          data-testid="tally-action"
          onClick={emit ? () => emit('actionActivated', { key: action.key ?? null }) : undefined}
          style={{
            ...resetButton, marginTop: 20, height: 45, borderRadius: 14, width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center',
            fontSize: 14, fontWeight: 800, background: 'var(--text)', color: 'var(--surface)',
            cursor: emit ? 'pointer' : 'default',
          }}
        >{action.label}</button>
      ) : null}
    </div>
  );
}

/**
 * MeterCard (L1) — one quantity read against its cap, and what it is made of.
 *
 * Eyebrow · the value at display size with its unit on the baseline · a thin
 * track filled to value/max · a row of part tiles (a label over a bold number
 * with its own unit suffix). StatHeadline says the number, Gauge says how much
 * of the cap it is, StatTileRow turns tiles into filters; this one puts the
 * reading, the cap and the breakdown on ONE card, and nothing on it is
 * tappable. The fill is clamped to the track — past the cap it turns warn
 * instead of drawing off the end. A null value is UNKNOWN: an em dash and no
 * fill, never a zero. Spacing is the design's own (8 · 10 · 12).
 */
function meterReading(raw) {
  const known = raw != null && raw !== '';
  if (!known) return { known: false, text: '—', number: null };
  const numeric = typeof raw === 'number'
    || (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw)));
  return numeric
    ? { known: true, text: fmt(num(raw)), number: num(raw) }
    : { known: true, text: String(raw), number: null };
}

function MeterCardBare({ props = {}, data, slots }) {
  const pushed = slots?.setMeter?.payload || {};
  const pick = (name) => (pushed[name] !== undefined ? pushed[name] : (props[name] !== undefined ? props[name] : data?.[name]));
  const reading = meterReading(pick('value'));
  const eyebrow = props.eyebrow ?? (data ? data.eyebrow : null);
  const unit = props.unit ?? (data ? data.unit : null);
  const rawMax = pick('max');
  const max = rawMax != null && rawMax !== '' ? num(rawMax) : 0;
  const ratio = reading.number != null && max > 0 ? reading.number / max : 0;
  const over = ratio > 1;
  const fill = Math.min(1, Math.max(0, ratio));
  const partSource = pick('parts');
  const parts = (Array.isArray(partSource) ? partSource : []).filter((p) => p && typeof p === 'object');
  const ellipsis = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {eyebrow ? (
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-3)', marginBottom: 8, ...ellipsis }}>{eyebrow}</span>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
        <span data-testid="meter-value" style={{
          fontSize: 48, lineHeight: 1, letterSpacing: -1, fontWeight: 800,
          color: reading.known ? 'var(--text)' : 'var(--text-3)',
        }}>{reading.text}</span>
        {unit ? (
          <span style={{ fontSize: 14, lineHeight: 1, fontWeight: 600, color: 'var(--text-3)' }}>{unit}</span>
        ) : null}
      </div>
      {max > 0 ? (
        <div data-testid="meter-track" style={{
          marginTop: 10, height: 6, borderRadius: 100, background: 'var(--surface-2)', overflow: 'hidden',
        }}>
          <div data-testid="meter-fill" style={{
            height: '100%', borderRadius: 100, width: `${Math.round(fill * 1000) / 10}%`,
            background: over ? 'var(--warn)' : 'var(--text)',
          }} />
        </div>
      ) : null}
      {parts.length ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, minWidth: 0 }}>
          {parts.map((p, i) => {
            const r = meterReading(p.value);
            return (
              <div
                key={p.key ?? `${p.label ?? ''}-${i}`}
                data-testid="meter-part"
                style={{
                  flex: 1, minWidth: 0, padding: 10, borderRadius: 10, background: 'var(--surface-2)',
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}
              >
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', ...ellipsis }}>{p.label ?? ''}</span>
                <span style={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
                  <span data-testid="meter-part-value" style={{
                    fontSize: 18, fontWeight: 800, letterSpacing: -0.5,
                    color: r.known ? toneColor(p.tone) : 'var(--text-3)',
                  }}>{r.text}</span>
                  {p.unit ? (
                    <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: -0.5, color: 'var(--text-3)' }}>{p.unit}</span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * CountsCard (L1) — a header and a row of equal counts, read side by side.
 *
 * Each tile is the value set large and centred over a small uppercase label
 * saying what it counts; a `tone` tints THAT tile's ground so one reading
 * stands out. Where TallyCard is one count with a thing to do about it, this
 * is several with nothing to do: the tiles are readings, not filters — nothing
 * is tappable, which is the whole difference from StatTileRow. A null value is
 * UNKNOWN: an em dash, never a zero. Spacing is the design's own (20 header to
 * row, 10 between tiles, 14/10 padding, 14 corners).
 */
function CountsCardBare({ props = {}, data, slots }) {
  const pushed = slots?.setCounts?.payload || {};
  const header = props.header ?? (data ? data.header : null);
  const source = pushed.counts !== undefined ? pushed.counts : (props.counts !== undefined ? props.counts : data?.counts);
  const counts = (Array.isArray(source) ? source : []).filter((c) => c && typeof c === 'object');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {header ? (
        <span style={{
          fontSize: 20, fontWeight: 800, letterSpacing: -0.5, color: 'var(--text)', marginBottom: 20,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{header}</span>
      ) : null}
      {counts.length ? (
        <div style={{ display: 'flex', gap: 10, minWidth: 0 }}>
          {counts.map((c, i) => {
            const r = meterReading(c.value);
            return (
              <div
                key={c.key ?? `${c.label ?? ''}-${i}`}
                data-testid="counts-tile"
                data-tone={c.tone || undefined}
                style={{
                  flex: 1, minWidth: 0, padding: '14px 10px', borderRadius: 14,
                  background: c.tone ? toneSoft(c.tone) : 'var(--surface-2)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                }}
              >
                <span data-testid="counts-value" style={{
                  fontSize: 26, lineHeight: 1, letterSpacing: -1, fontWeight: 800,
                  color: r.known ? 'var(--text)' : 'var(--text-3)',
                }}>{r.text}</span>
                <span style={{
                  fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text-3)',
                  maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{c.label ?? ''}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
export const DayCells = ownCard(DayCellsBare, 'DayCells');
export const StatHeadline = ownCard(StatHeadlineBare, 'StatHeadline');
export const DayBars = ownCard(DayBarsBare, 'DayBars');
export const TallyCard = ownCard(TallyCardBare, 'TallyCard');
export const MeterCard = ownCard(MeterCardBare, 'MeterCard');
export const CountsCard = ownCard(CountsCardBare, 'CountsCard');
